<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { CalendarClock, CircleDollarSign, LoaderCircle, ReceiptText, Sparkles } from 'lucide-vue-next'
import { useToast } from 'primevue/usetoast'

import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import { useAiCreditsCheckout } from '@/composables/useAiCreditsCheckout'
import { useAiService } from '@/services/ai/useAiService'
import type { IAiTenantSummaryResponse } from '@/services/ai/interfaces/ai-credit.interface'
import type { IAiCreditTokenPackage } from '@/services/payment/interfaces/payment.interface'
import { usePaymentService } from '@/services/payment/usePaymentService'

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const toast = useToast()
const { getTenantSummary } = useAiService()
const { getAiCreditTokenPackages } = usePaymentService()
const { isSubmitting, startCheckout } = useAiCreditsCheckout()
const summary = ref<IAiTenantSummaryResponse | null>(null)
const packages = ref<IAiCreditTokenPackage[]>([])
const isLoading = ref(false)
const loadError = ref<string | null>(null)
const selectedPackageId = ref<string | null>(null)

const visible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const number = (value: number | string | null | undefined) =>
  value === null || value === undefined
    ? '--'
    : new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Number(value))

const summaryItems = computed(() => [
  {
    label: 'Saldo disponible',
    value: number(summary.value?.available_balance),
    unit: 'tokens',
    icon: CircleDollarSign,
    iconClass: 'text-primary-500',
  },
  {
    label: 'Consumo mensual',
    value: number(summary.value?.consumed_tokens),
    unit: 'tokens',
    icon: ReceiptText,
    iconClass: 'text-emerald-500',
  },
  {
    label: 'Días restantes',
    value: number(summary.value?.days_until_plan_renewal),
    unit: 'días',
    icon: CalendarClock,
    iconClass: 'text-sky-500',
  },
  {
    label: 'Plan actual',
    value: summary.value?.plan_name || '--',
    unit: summary.value?.plan_tokens ? `${number(summary.value.plan_tokens)} tokens` : '',
    icon: CircleDollarSign,
    iconClass: 'text-amber-500',
  },
])

const load = async () => {
  isLoading.value = true
  loadError.value = null

  try {
    const [nextSummary, nextPackages] = await Promise.all([
      getTenantSummary(),
      getAiCreditTokenPackages(),
    ])
    summary.value = nextSummary
    packages.value = nextPackages
  } catch {
    loadError.value = 'No se pudo cargar la información de tokens.'
  } finally {
    isLoading.value = false
  }
}

const buy = async (packageId: string) => {
  if (isSubmitting.value) return

  selectedPackageId.value = packageId
  try {
    await startCheckout(packageId)
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'No se pudo iniciar el pago.',
      life: 5000,
    })
  } finally {
    selectedPackageId.value = null
  }
}

watch(
  () => props.modelValue,
  (value) => {
    if (value) void load()
  },
)
</script>

<template>
  <AppDialog
    v-model:modelValue="visible"
    width="72rem"
    :draggable="false"
    :resizable="false"
    custom-class="ai-credits-token-dialog"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <div class="flex size-11 items-center justify-center rounded-xl bg-[var(--p-primary-color)] text-sm font-bold text-[var(--p-primary-contrast-color)] shadow-sm">
          IA+
        </div>
        <span class="text-lg font-semibold text-slate-900 dark:text-slate-50">Nebula AI</span>
      </div>
    </template>

    <template #content>
      <div v-if="isLoading" class="flex min-h-64 items-center justify-center text-slate-500 dark:text-slate-400">
        <LoaderCircle class="size-5 animate-spin" />
      </div>

      <div v-else-if="loadError" class="space-y-3 rounded-xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-500/20 dark:bg-rose-500/10">
        <p class="text-sm text-rose-700 dark:text-rose-300">{{ loadError }}</p>
        <button type="button" class="rounded-lg border border-rose-300 px-3 py-2 text-sm font-medium text-rose-700 dark:border-rose-400/50 dark:text-rose-300" @click="load">
          Reintentar
        </button>
      </div>

      <div v-else class="space-y-7">
        <section>
          <h2 class="text-base font-semibold text-slate-900 dark:text-slate-50">Resumen</h2>
          <div class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <article
              v-for="item in summaryItems"
              :key="item.label"
              class="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
            >
              <div class="flex items-center justify-between gap-3">
                <p class="text-xs text-slate-500 dark:text-slate-400">{{ item.label }}</p>
                <component :is="item.icon" class="size-4" :class="item.iconClass" />
              </div>
              <div class="mt-3 flex items-baseline gap-1.5">
                <p class="text-lg font-semibold text-slate-900 dark:text-slate-50">{{ item.value }}</p>
                <p v-if="item.unit" class="text-xs text-slate-500 dark:text-slate-400">{{ item.unit }}</p>
              </div>
            </article>
          </div>
        </section>

        <section>
          <h2 class="text-base font-semibold text-slate-900 dark:text-slate-50">Comprar más tokens</h2>
          <div v-if="packages.length" class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="tokenPackage in packages"
              :key="tokenPackage.id"
              class="flex min-h-52 flex-col items-center rounded-xl border border-slate-200 bg-white p-4 text-center dark:border-slate-700 dark:bg-slate-900"
            >
              <Sparkles class="size-5 text-[var(--p-primary-color)]" />
              <p class="mt-2 text-lg font-semibold text-[var(--p-primary-color)]">{{ number(tokenPackage.token_amount) }}</p>
              <p class="text-xs text-slate-500 dark:text-slate-400">tokens · vigencia {{ tokenPackage.validity_days }} días</p>
              <p class="mt-5 text-base font-semibold text-[var(--p-primary-color)]">${{ number(tokenPackage.price) }} {{ tokenPackage.currency }}</p>
              <button
                type="button"
                :disabled="isSubmitting"
                class="mt-auto w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
                @click="buy(tokenPackage.id)"
              >
                {{ selectedPackageId === tokenPackage.id ? 'Procesando' : 'Comprar' }}
              </button>
            </article>
          </div>
          <p v-else class="mt-3 text-sm text-slate-500 dark:text-slate-400">No hay paquetes disponibles.</p>
        </section>
      </div>
    </template>
  </AppDialog>
</template>
